import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

const hexToHsl = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  let [r, g, b] = [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) { case r: h = (g - b) / d + (g < b ? 6 : 0); break; case g: h = (b - r) / d + 2; break; default: h = (r - g) / d + 4; }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const applyBranding = (b) => {
  if (!b) return;
  const root = document.documentElement;
  if (b.primaryColor) { root.style.setProperty("--primary-color", b.primaryColor); const hsl = hexToHsl(b.primaryColor); if (hsl) { root.style.setProperty("--primary", hsl); root.style.setProperty("--ring", hsl); } }
  if (b.secondaryColor) root.style.setProperty("--secondary-color", b.secondaryColor);
  if (b.accentColor) root.style.setProperty("--accent-color", b.accentColor);
  if (b.appName) document.title = b.appName;
  if (b.faviconUrl) { let l = document.querySelector("link[rel~='icon']"); if (!l) { l = document.createElement("link"); l.rel = "icon"; document.head.appendChild(l); } l.href = b.faviconUrl.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${b.faviconUrl}` : b.faviconUrl; }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("tmc_token");
    if (!token) { setUser(null); setLoading(false); return null; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data); setBranding(data.branding); applyBranding(data.branding);
      return data;
    } catch { localStorage.removeItem("tmc_token"); setUser(null); return null; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (token) => { localStorage.setItem("tmc_token", token); return refresh(); }, [refresh]);
  const logout = useCallback(async () => { try { await api.post("/auth/logout"); } catch {} localStorage.removeItem("tmc_token"); localStorage.removeItem("tmc_admin_token"); setUser(null); }, []);
  const has = useCallback((perm) => !!user && (user.effectiveRoles?.includes("super_admin") || user.effectiveRoles?.includes("admin") || user.permissions?.includes(perm)), [user]);
  const hasRole = useCallback((role) => !!user?.effectiveRoles?.includes(role), [user]);

  const value = useMemo(() => ({ user, branding, loading, login, logout, refresh, has, hasRole, setBranding: (b) => { setBranding(b); applyBranding(b); } }), [user, branding, loading, login, logout, refresh, has, hasRole]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
